import { TraitItem } from "../../src/types/fashionIndex/InventoryTypes"
import {
	allWearableCategories,
	resolveEaster26Filename,
} from "../../utils/dynamic-equipment/helpers"

/** Extra roots seen on drop_type trees (parent names) besides wearable slots. */
const EXTRA_CANONICAL_CATEGORIES = [
	"Haircut",
	"Tattoos",
	"Implants",
	"Face Addition",
] as const

const CANONICAL_DROP_CATEGORY = new Set<string>([
	...allWearableCategories,
	...EXTRA_CANONICAL_CATEGORIES,
])

/**
 * Child drop types the backend lets sit on the hape together with their parent
 * category (Outerwear + Clothing). Closet tabs still use the parent.
 */
const COEXIST_WITH_PARENT_CATEGORY = new Set(["outerwear"])

function normalizeCategoryKey(category: string): string {
	return category.trim().toLowerCase()
}

function dropTypeName(dropType: any): string {
	if (!dropType || typeof dropType !== "object") return ""
	const n = typeof dropType.name === "string" ? dropType.name.trim() : ""
	if (!n) return ""
	return stripLayerSuffix(n) || n
}

function isCoexistChildName(name: string): boolean {
	return COEXIST_WITH_PARENT_CATEGORY.has(name.trim().toLowerCase())
}

/** Distinct on-chain positions mean the child can stay equipped next to its parent. */
function childHasDistinctPosition(dropType: any): boolean {
	if (!dropType || typeof dropType !== "object" || !dropType.parent) return false
	const childPos = Number(dropType.position_id)
	const parentPos = Number(dropType.parent.position_id)
	return (
		Number.isFinite(childPos) &&
		Number.isFinite(parentPos) &&
		childPos > 0 &&
		parentPos > 0 &&
		childPos !== parentPos
	)
}

function walkDropTypeChain(
	dropType: any,
	visit: (dt: any, name: string) => string | undefined,
): string {
	if (!dropType || typeof dropType !== "object") return ""
	const seen = new Set<object>()
	let depth = 0
	for (let dt: any = dropType; dt && typeof dt === "object" && depth < 12; dt = dt.parent) {
		if (seen.has(dt)) break
		seen.add(dt)
		depth += 1
		const name = dropTypeName(dt)
		if (!name) continue
		const hit = visit(dt, name)
		if (hit) return hit
	}
	return ""
}

function canonicalCategoryFromDropType(dropType: any): string {
	let best = ""
	walkDropTypeChain(dropType, (dt, name) => {
		const raw = typeof dt.name === "string" ? dt.name.trim() : name
		if (CANONICAL_DROP_CATEGORY.has(raw) || CANONICAL_DROP_CATEGORY.has(name)) {
			best = CANONICAL_DROP_CATEGORY.has(raw) ? raw : name
		}
		return undefined
	})
	if (best) return best
	if (dropType?.parent?.name) return stripLayerSuffix(String(dropType.parent.name))
	if (dropType?.name) return stripLayerSuffix(String(dropType.name))
	return ""
}

/** Closet tab for this drop. Outerwear stays under Clothing; no new category tab. */
export function closetCategoryFromDropType(dropType: any): string {
	return canonicalCategoryFromDropType(dropType)
}

/** Outerwear is a Clothing-tab overlay, not its own closet. */
const CLOSET_TAB_ALIASES: Record<string, string> = {
	outerwear: "clothing",
}

/** Closet tab used for ticks and exclusive equip. Outerwear shares Clothing. */
export function closetTabKey(trait?: { category?: string; drop_type?: any } | null): string {
	const fromDrop = closetCategoryFromDropType(trait?.drop_type)
	const key = normalizeCategoryKey(fromDrop || trait?.category || "")
	return CLOSET_TAB_ALIASES[key] || key
}

/**
 * First child type that is allowed to stay equipped next to its parent category.
 * Outerwear is named; other overlay types use a distinct position_id from the parent.
 */
function coexistChildFromDropType(dropType: any): string {
	return walkDropTypeChain(dropType, (dt, name) => {
		if (isCoexistChildName(name)) return name
		if (childHasDistinctPosition(dt) && name !== canonicalCategoryFromDropType(dt)) {
			return name
		}
		return undefined
	})
}

/** Main / Back / Front are generator layers, not the trait category. */
export function stripLayerSuffix(name: unknown): string {
	if (typeof name !== "string") return ""
	return name.trim().replace(/\s+(Main|Back|Front)$/i, "").trim()
}

/**
 * Slot used for equipped traits / merge.
 * Overlay children such as Outerwear keep their own name so they are not
 * collapsed into Clothing. Closet tabs still use closetCategoryFromDropType.
 */
export function resolveCategoryFromDropType(dropType: any): string {
	const coexist = coexistChildFromDropType(dropType)
	if (coexist) return coexist
	return canonicalCategoryFromDropType(dropType)
}

/** Merge / worn-slot key. Overlay children do not share a key with their parent. */
export function equippedSlotKey(trait?: { category?: string; drop_type?: any } | null): string {
	if (!trait) return ""
	const fromDrop = resolveCategoryFromDropType(trait.drop_type)
	return normalizeCategoryKey(fromDrop || trait.category || "")
}

/**
 * On-chain / listing metadata (Django "native" traits). Same shape as OpenSea attributes.
 */
export function parseHapeMetadataToTraitItems(metadata: unknown): TraitItem[] {
	if (metadata == null) return []
	let arr: any[] = []
	if (typeof metadata === "string") {
		try {
			arr = JSON.parse(metadata)
		} catch {
			return []
		}
	} else if (Array.isArray(metadata)) {
		arr = metadata
	} else {
		return []
	}

	const out: TraitItem[] = []
	for (const a of arr) {
		if (!a || typeof a !== "object") continue
		const category =
			a.trait_type ?? a.traitType ?? a.category ?? a.trait_category
		const value = a.value
		if (category == null || value === undefined || value === null) continue
		const catStr = String(category).trim()
		const valStr = String(value).trim()
		if (!catStr || !valStr) continue

		out.push({
			equip_id: `meta-${catStr}`,
			is_locked: false,
			equipped_on_other_hape_id: null,
			is_equipped_on_this_hape: true,
			is_available_to_equip: false,
			category: catStr,
			value: valStr,
			rarity_score: typeof a.rarity_score === "number" ? a.rarity_score : 0,
			rarity_type:
				typeof a.rarity_type === "string" && a.rarity_type
					? a.rarity_type
					: "native",
			trait_type: "Native",
			drop_type: undefined,
			image: a.image_url || a.image || "",
			token_id: "",
			collection_name: "",
		})
	}
	return out
}

/**
 * Native/metadata first, then equipped drops override per slot.
 * Overlay children (Outerwear) keep a separate key from Clothing so both survive.
 */
export function mergeMetadataAndEquippedTraits(
	fromMetadata: TraitItem[],
	fromDrops: TraitItem[],
): TraitItem[] {
	const map = new Map<string, TraitItem>()
	for (const t of fromMetadata) {
		map.set(equippedSlotKey(t), t)
	}
	for (const t of fromDrops) {
		map.set(equippedSlotKey(t), t)
	}
	return Array.from(map.values())
}

/**
 * Maps raw drop rows from GET /hape/equipped/drops (or original equipped) to TraitItem.
 * Resolves the equipped slot (Outerwear stays Outerwear; Tops still map to Clothing).
 *
 * Pipeline verification (same hape_uuid):
 * 1. Network: raw `drops` in API JSON
 * 2. This function’s output — any `category === "Clothing"`
 * 3. `generateHapeImage` POST `attributes` / Django `traits_input`
 * If (1) has clothing rows but (2) does not → mapping bug. If (1) has none → DB / hape_drop / API.
 */
export function mapEquippedDropsToTraitItems(drops: any[]): TraitItem[] {
	if (!drops || !Array.isArray(drops) || drops.length === 0) {
		return []
	}
	const newDrops: TraitItem[] = []
	for (const drop of drops) {
		try {
			let rarity_type = "common"
			if (drop.attributes && Array.isArray(drop.attributes)) {
				for (const attribute of drop.attributes) {
					if (attribute.trait_type === "Rarity") {
						rarity_type = attribute.value
					}
				}
			}

			const category = resolveCategoryFromDropType(drop.drop_type)

			const ownedRaw = Number(drop.owned_quantity ?? drop.balance)
			const newDrop: TraitItem = {
				equip_id: drop.id,
				is_locked: false,
				equipped_on_other_hape_id: null,
				is_equipped_on_this_hape: true,
				is_available_to_equip: false,
				category,
				value: drop.name || "",
				rarity_score: drop.rarity_score || 0,
				rarity_type,
				trait_type:
					drop.series_type === "Global" ? "Global" : drop.is_nft ? "NFT" : "",
				drop_type: drop.drop_type,
				image: drop.image || "",
				token_id: drop.token_id || "",
				collection_name:
					drop.collection && drop.collection.name ? drop.collection.name : "",
				balance: Number.isFinite(ownedRaw) && ownedRaw > 0 ? ownedRaw : 1,
			}
			newDrops.push(newDrop)
		} catch (error) {
			console.error("mapEquippedDropsToTraitItems: error processing drop:", drop, error)
		}
	}
	return newDrops
}

export function mergeTraitsForGenerator(
	metadata: unknown,
	drops: any[],
): TraitItem[] {
	const metaTraits = parseHapeMetadataToTraitItems(
		metadata === "" ? null : metadata,
	)
	const dropTraits = mapEquippedDropsToTraitItems(drops || [])
	return mergeMetadataAndEquippedTraits(metaTraits, dropTraits)
}

/** Closet tab names that share an original-trait slot. */
const CATEGORY_ALIASES: Record<string, string[]> = {
	clothing: ["clothes"],
	clothes: ["clothing"],
	headwear: ["hat", "hats"],
	hat: ["headwear", "hats"],
	hats: ["headwear", "hat"],
	earrings: ["earring"],
	earring: ["earrings"],
	wristband: ["bracelets", "bracelet"],
	bracelets: ["wristband", "bracelet"],
	bracelet: ["bracelets", "wristband"],
	"finger rings": ["finger ring", "fingerrings"],
	"finger ring": ["finger rings", "fingerrings"],
	footwear: ["shoes"],
	shoes: ["footwear"],
	lowers: ["pants"],
	pants: ["lowers"],
}

function categoryAliasSet(category: string): Set<string> {
	const key = normalizeCategoryKey(category)
	return new Set([key, ...(CATEGORY_ALIASES[key] || [])].filter(Boolean))
}

/** True when two wardrobe / metadata category names are the same slot. */
export function categoriesMatch(a?: string | null, b?: string | null): boolean {
	const left = normalizeCategoryKey(a || "")
	const right = normalizeCategoryKey(b || "")
	if (!left || !right) return false
	if (left === right) return true
	return categoryAliasSet(left).has(right)
}

export function isNativeLikeTrait(
	trait?: { equip_id?: string | number; trait_type?: string; rarity_type?: string } | null,
): boolean {
	if (!trait || typeof trait !== "object") return false
	if (String(trait.equip_id ?? "").startsWith("meta-")) return true
	if (String(trait.equip_id ?? "").startsWith("fbh-")) return true
	return String(trait.trait_type || "").toLowerCase() === "native"
}

/** Original trait for this closet category, from the Hape's metadata. */
export function nativeTraitForCategory(
	metadata: unknown,
	category: string,
): TraitItem | null {
	const key = category.trim().toLowerCase()
	if (!key) return null
	const aliases = categoryAliasSet(category)
	const found =
		parseHapeMetadataToTraitItems(metadata).find((trait) =>
			aliases.has(normalizeCategoryKey(trait.category)),
		) ?? null
	if (!found) return null
	return { ...found, category: category.trim() || found.category }
}

function markMappedDropsAsNative(traits: TraitItem[]): TraitItem[] {
	return traits.map((trait) => {
		const category = String(trait.category || "").trim()
		return {
			...trait,
			trait_type: "Native" as const,
			rarity_type:
				typeof trait.rarity_type === "string" && trait.rarity_type
					? trait.rarity_type
					: "native",
			equip_id: `meta-${category || trait.equip_id}`,
			is_equipped_on_this_hape: true,
			is_available_to_equip: false,
		}
	})
}

/** `/hape/original/equipped/drops` may return drop rows or metadata attributes. */
export function parseOriginalEquippedPayload(data: unknown): TraitItem[] {
	if (!data || typeof data !== "object") return []
	const payload = data as {
		original_drops?: unknown
		drops?: unknown
		attributes?: unknown
	}
	const dropRows = Array.isArray(payload.original_drops)
		? payload.original_drops
		: Array.isArray(payload.drops)
			? payload.drops
			: []
	if (dropRows.length > 0) {
		const fromDrops = markMappedDropsAsNative(mapEquippedDropsToTraitItems(dropRows))
		if (fromDrops.some((trait) => trait.category)) return fromDrops
		const fromRowsAsMeta = parseHapeMetadataToTraitItems(dropRows)
		if (fromRowsAsMeta.length > 0) return fromRowsAsMeta
	}
	return parseHapeMetadataToTraitItems(payload.attributes)
}

const nativeTraitsByHapeId = new Map<string, TraitItem[]>()

export function rememberNativeTraits(hapeId: string, traits: TraitItem[]): void {
	const key = String(hapeId || "").trim()
	if (!key) return
	nativeTraitsByHapeId.set(key, Array.isArray(traits) ? traits : [])
}

export function rememberOriginalEquippedPayload(hapeId: string, data: unknown): TraitItem[] {
	const parsed = parseOriginalEquippedPayload(data)
	rememberNativeTraits(
		hapeId,
		fillMissingNativeCategories(rememberedNativeTraits(hapeId), parsed),
	)
	return parsed
}

export function rememberedNativeTraits(hapeId: string): TraitItem[] {
	return nativeTraitsByHapeId.get(String(hapeId || "").trim()) || []
}

export function resetNativeTraitCachesForTests(): void {
	nativeTraitsByHapeId.clear()
}

/**
 * Overlay equipped drops onto originals. A drop that is the same value as the
 * origin row (reset / original_drops fallback) keeps the native identity so the
 * first wardrobe cell stays ticked.
 */
export function overlayEquippedDropsOnNatives(
	natives: TraitItem[],
	drops: TraitItem[],
): TraitItem[] {
	const map = new Map<string, TraitItem>()
	for (const trait of natives || []) {
		const key = equippedSlotKey(trait)
		if (key) map.set(key, trait)
	}
	for (const trait of drops || []) {
		const key = equippedSlotKey(trait)
		if (!key) continue
		const existing = map.get(key)
		if (
			existing &&
			isNativeLikeTrait(existing) &&
			existing.value &&
			existing.value === trait.value
		) {
			continue
		}
		map.set(key, trait)
	}
	return Array.from(map.values())
}

/** Keep metadata rows; add original-drop rows only for empty slots. */
export function fillMissingNativeCategories(
	base: TraitItem[],
	extra: TraitItem[],
): TraitItem[] {
	const map = new Map<string, TraitItem>()
	for (const trait of extra || []) {
		const key = equippedSlotKey(trait)
		if (key) map.set(key, trait)
	}
	for (const trait of base || []) {
		const key = equippedSlotKey(trait)
		if (key) map.set(key, trait)
	}
	return Array.from(map.values())
}

export function nativePlaceholderTrait(category: string, value = ""): TraitItem {
	const catStr = String(category || "").trim()
	return {
		equip_id: `meta-${catStr}`,
		is_locked: false,
		equipped_on_other_hape_id: null,
		is_available_to_equip: false,
		is_equipped_on_this_hape: false,
		category: catStr,
		value: value || "Original",
		rarity_score: 0,
		rarity_type: "native",
		trait_type: "Native",
		drop_type: undefined,
		image: "",
		token_id: "",
		collection_name: "",
	}
}

function nativeFromList(traits: TraitItem[] | undefined, category: string): TraitItem | null {
	const aliases = categoryAliasSet(category)
	const found = (traits || []).find(
		(trait) =>
			aliases.has(normalizeCategoryKey(trait.category)) && isNativeLikeTrait(trait),
	)
	if (!found) return null
	return {
		...found,
		category: category.trim() || found.category,
		trait_type: "Native",
		equip_id: String(found.equip_id || "").startsWith("meta-")
			? found.equip_id
			: `meta-${category.trim() || found.category}`,
	}
}

/**
 * First wardrobe cell for a category. Always returns a row so the silhouette
 * is present even when listing metadata is missing.
 */
export function nativeTraitForClosetSlot(
	metadata: unknown,
	category: string,
	extraNativeTraits: TraitItem[] = [],
): TraitItem {
	return (
		nativeFromList(extraNativeTraits, category) ||
		nativeTraitForCategory(metadata, category) ||
		nativePlaceholderTrait(category)
	)
}

/** True when the saved outfit is still the original trait for that category. */
export function isNativeTraitEquipped(
	equipped: TraitItem[] | undefined,
	nativeTrait: TraitItem,
): boolean {
	if (!Array.isArray(equipped)) return nativeTrait.is_equipped_on_this_hape
	return equipped.some((trait) => {
		if (!categoriesMatch(trait.category, nativeTrait.category)) return false
		if (String(trait.equip_id) === String(nativeTrait.equip_id)) return true
		return (
			trait.value === nativeTrait.value &&
			(trait.trait_type === "Native" ||
				String(trait.rarity_type).toLowerCase() === "native")
		)
	})
}

/** Closet / generator slots that are scene backdrops, not wearable body slots. */
const BACKGROUND_SLOT_NAMES = new Set(["background", "special-2"])

/**
 * Special-closet props that only have half-body art. Scene items in that same
 * closet (lanterns, easter eggs) must still render in full body.
 */
const HALF_BODY_ONLY_SPECIAL_VALUES = new Set([
	"sword (secret society)",
	"katanas (black)",
	"katanas (white)",
	"x23 iii.o snowboard (white)",
])

/** Named scene drops, in case category mapping is missing on the row. */
const FULL_BODY_BACKGROUND_SCENE_VALUES = new Set([
	"cny24 lanterns (fortune red)",
	"easter eggs",
])

function traitValueKey(trait?: { value?: string } | null): string {
	return String(trait?.value || "").trim().toLowerCase()
}

/**
 * Generator / closet slot for this row. TraitItem uses category for the slot
 * and trait_type for Native/NFT/Global; generator rows use trait_type as the slot.
 */
function generatorSlotName(trait?: {
	category?: string
	trait_type?: string
} | null): string {
	const kind = String(trait?.trait_type || "").trim().toLowerCase()
	const category = String(trait?.category || "").trim().toLowerCase()
	if (["native", "nft", "global", ""].includes(kind)) return category
	return kind || category
}

function dropTypeIsBackgroundLayer(dropType: unknown): boolean {
	return Boolean(
		walkDropTypeChain(dropType, (_dt, name) => {
			const raw = name.trim().toLowerCase()
			// Exact Background slot, not wearable "Lowers Back" layers.
			if (raw === "background" || raw === "special back") return name
			return undefined
		}),
	)
}

/**
 * Scene backgrounds (CNY lanterns, Easter Eggs, native Background) keep their
 * full-body art. Sword / snowboard stay half-body-only even though they share
 * the Special closet tab.
 */
export function isBackgroundSceneTrait(trait?: {
	category?: string
	trait_type?: string
	value?: string
	drop_type?: unknown
} | null): boolean {
	if (!trait || typeof trait !== "object") return false
	const value = traitValueKey(trait)
	if (value && HALF_BODY_ONLY_SPECIAL_VALUES.has(value)) return false
	if (value && FULL_BODY_BACKGROUND_SCENE_VALUES.has(value)) return true
	const slot = generatorSlotName(trait)
	if (BACKGROUND_SLOT_NAMES.has(slot)) return true
	if (slot === "special") return true
	return dropTypeIsBackgroundLayer(trait.drop_type)
}

export function isUnavailableInFullBody(trait?: {
	portrait_only?: boolean
	trait_type?: string
	category?: string
	value?: string
	drop_type?: unknown
} | null): boolean {
	if (!trait || typeof trait !== "object") return false
	if (isBackgroundSceneTrait(trait)) return false
	if (trait.portrait_only) return true
	return String(trait.trait_type || "").toLowerCase() === "global"
}

function traitBelongsToClosetCategory(
	trait: TraitItem,
	category: string,
): boolean {
	if (categoriesMatch(trait.category, category)) return true
	const closet = closetCategoryFromDropType(trait.drop_type)
	if (closet && categoriesMatch(closet, category)) return true
	// Scene drops can sit on a Background slot while the closet tab is Special.
	return categoriesMatch(category, "Special") && isBackgroundSceneTrait(trait)
}

export function wornDropForCategory(
	equipped: TraitItem[] | undefined,
	category: string,
): TraitItem | undefined {
	return (equipped || []).find(
		(trait) =>
			!isNativeLikeTrait(trait) && traitBelongsToClosetCategory(trait, category),
	)
}

/** Prefer the published outfit so a body-mode toggle cannot fall back to a stale query. */
export function traitsForWardrobeTicks(
	queryTraits: TraitItem[] | undefined,
	wornTraits?: TraitItem[] | null,
): TraitItem[] | undefined {
	if (Array.isArray(wornTraits) && wornTraits.length > 0) return wornTraits
	return queryTraits
}

/** CSS extras for scene backgrounds the generator does not composite. */
export function sceneLayerExtras(
	traits?: Array<{ value?: string; category?: string; trait_type?: string }> | null,
	hapeId?: string,
): { lanterns: boolean; easterEggs: boolean; easter26Filename: string } {
	const list = Array.isArray(traits) ? traits : []
	const lanterns = list.some(
		(trait) => traitValueKey(trait) === "cny24 lanterns (fortune red)",
	)
	const easterEggs = list.some((trait) => traitValueKey(trait) === "easter eggs")
	return {
		lanterns,
		easterEggs,
		// Lanterns win when both scenes are present. Eggs use the same CSS
		// cover overlay in half-body and full-body.
		easter26Filename:
			!lanterns && easterEggs ? resolveEaster26Filename(list, hapeId) : "",
	}
}

/**
 * Native cell is ticked when that slot is showing the original on the preview.
 * Full-body hides global / portrait-only wearables (not scene backgrounds), so
 * the original cell is active even while those drops stay equipped for half-body.
 */
export function isNativeSlotDisplayed(
	equipped: TraitItem[] | undefined,
	nativeTrait: TraitItem,
	renderMode?: string,
): boolean {
	if (!Array.isArray(equipped)) return nativeTrait.is_equipped_on_this_hape
	const worn = wornDropForCategory(equipped, nativeTrait.category)
	if (!worn) return true
	if (renderMode === "full_body" && isUnavailableInFullBody(worn)) return true
	return isNativeTraitEquipped(equipped, nativeTrait)
}

function generatorAttributeKey(traitType: string): string {
	return String(traitType || "").trim().toLowerCase()
}

function generatorSlotFromFiller(attr?: {
	category?: string
	trait_type?: string
}): string {
	const kind = String(attr?.trait_type || "").trim().toLowerCase()
	const fromCategory = String(attr?.category || "").trim()
	const fromTraitType = String(attr?.trait_type || "").trim()
	return fromCategory && ["native", "nft", "global", ""].includes(kind)
		? fromCategory
		: fromCategory || fromTraitType
}

/** Fill generator slots the worn list left empty, using original / FBH traits. */
export function fillMissingGeneratorSlots(
	attributes: Array<{ trait_type: any; value: any }>,
	fillers: Array<{ category?: string; trait_type?: string; value?: string }> = [],
): Array<{ trait_type: any; value: any }> {
	const next = [...(attributes || [])]
	const used = new Set(
		next
			.map((attr) => generatorAttributeKey(String(attr?.trait_type || "")))
			.filter(Boolean),
	)
	for (const attr of fillers || []) {
		const traitType = generatorSlotFromFiller(attr)
		const value = String(attr?.value || "").trim()
		if (!traitType || !value || value.toLowerCase() === "original") continue
		const key = generatorAttributeKey(traitType)
		if (!key || used.has(key)) continue
		next.push({ trait_type: traitType, value })
		used.add(key)
	}
	return next
}

/**
 * Full-body preview: skip traits with no FBH art (not scene backgrounds), then
 * fill that slot from the hape's original trait and leftover FBH natives.
 */
export function applyFullBodyOriginalFallback(
	wornGeneratorTraits: Array<{
		trait_type?: string
		value?: string
		portrait_only?: boolean
	}>,
	originalTraits: Array<{ category?: string; trait_type?: string; value?: string }> = [],
	fbhAttributes: Array<{ trait_type?: string; value?: string }> = [],
): Array<{ trait_type: any; value: any }> {
	const attributes: Array<{ trait_type: any; value: any }> = []

	for (const trait of wornGeneratorTraits || []) {
		if (!trait || typeof trait !== "object") continue
		if (isUnavailableInFullBody(trait)) continue
		if (trait.trait_type == null && trait.value == null) continue
		attributes.push({
			trait_type: trait.trait_type,
			value: trait.value,
		})
	}

	return fillMissingGeneratorSlots(
		fillMissingGeneratorSlots(attributes, originalTraits),
		fbhAttributes,
	)
}

/** Stable slice for React Query keys (full JSON when object/array). */
export function metadataQueryKeyPart(metadata: unknown): string {
	if (metadata == null) return ""
	if (typeof metadata === "string") return metadata
	try {
		return JSON.stringify(metadata)
	} catch {
		return ""
	}
}

/** Serialize API `attributes` / `traits` for `hapeMetadataAttributesAtom` (string primitive). */
export function stringifyHapeMetadataInput(meta: unknown): string {
	if (meta == null || meta === "") return ""
	if (typeof meta === "string") return meta
	try {
		return JSON.stringify(meta)
	} catch {
		return ""
	}
}
